// © 2026 AttainIP Engineering
import { Fragment, useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, ChevronRight, ChevronDown } from 'lucide-react';
import { fetchEvents } from '@/api/queries';
import { Card } from '@/components/Card';
import { AuditEventDetail } from '@/components/AuditEventDetail';

const OUTCOME_FILTERS = [
  { value: '', label: 'All' },
  { value: 'success', label: 'Success' },
  { value: 'failure', label: 'Failure' },
];

export function EventsPage() {
  const [search, setSearch] = useState('');
  const [outcomeFilter, setOutcomeFilter] = useState('');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const { data, isLoading } = useQuery({
    queryKey: ['events'],
    queryFn: () => fetchEvents({ limit: 200 }),
  });

  const events = data?.data || data || [];

  const filtered = useMemo(() => {
    let items = Array.isArray(events) ? events : [];
    if (outcomeFilter) {
      items = items.filter((evt: any) => evt.outcome === outcomeFilter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      items = items.filter((evt: any) =>
        (evt.event || '').toLowerCase().includes(q) ||
        (evt.actor_name || '').toLowerCase().includes(q) ||
        (evt.actor_email || '').toLowerCase().includes(q) ||
        (evt.actor_id || '').toLowerCase().includes(q) ||
        (evt.source_service || '').toLowerCase().includes(q) ||
        (evt.resource_type || '').toLowerCase().includes(q) ||
        (evt.resource_id || '').toLowerCase().includes(q)
      );
    }
    return items;
  }, [events, search, outcomeFilter]);

  if (isLoading) return <p className="text-gray-500">Loading events...</p>;

  return (
    <div>
      <h1 className="text-xl font-bold mb-6">Audit Log ({Array.isArray(events) ? events.length : 0})</h1>

      <div className="flex gap-2 mb-4">
        {OUTCOME_FILTERS.map(({ value, label }) => (
          <button
            key={value}
            onClick={() => setOutcomeFilter(value)}
            className={`px-3 py-1 rounded text-xs font-medium ${
              outcomeFilter === value ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="relative mb-4">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by event, actor, service, or resource..."
          className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      <Card>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-gray-500">
              <th className="pb-2 font-medium w-6"></th>
              <th className="pb-2 font-medium">Event</th>
              <th className="pb-2 font-medium">Outcome</th>
              <th className="pb-2 font-medium">Actor</th>
              <th className="pb-2 font-medium">Service</th>
              <th className="pb-2 font-medium">Resource</th>
              <th className="pb-2 font-medium">Time</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((evt: any) => {
              const isOpen = !!expanded[evt.id];
              const hasDetail =
                (evt.metadata && Object.keys(evt.metadata).length > 0) ||
                evt.correlation_id || evt.idempotency_key;
              const toggle = () =>
                setExpanded((s) => ({ ...s, [evt.id]: !s[evt.id] }));
              return (
                <Fragment key={evt.id}>
                  <tr
                    className={`border-b last:border-0 hover:bg-gray-50 ${hasDetail ? 'cursor-pointer' : ''}`}
                    onClick={hasDetail ? toggle : undefined}
                  >
                    <td className="py-2 text-gray-400">
                      {hasDetail ? (
                        isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />
                      ) : null}
                    </td>
                    <td className="py-2 font-mono text-xs">{evt.event || '—'}</td>
                    <td className="py-2">
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        evt.outcome === 'success' ? 'bg-green-100 text-green-700' :
                        evt.outcome === 'failure' ? 'bg-red-100 text-red-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {evt.outcome || '—'}
                      </span>
                    </td>
                    <td className="py-2 text-xs">{evt.actor_name || evt.actor_email || (evt.actor_id ? `${evt.actor_id.slice(0, 8)}...` : '—')}</td>
                    <td className="py-2 text-xs">{evt.source_service || '—'}</td>
                    <td className="py-2 font-mono text-xs text-gray-400">{evt.resource_type ? `${evt.resource_type}/${evt.resource_id || ''}` : (evt.metadata?.session_id ? `session/${evt.metadata.session_id}` : '—')}</td>
                    <td className="py-2 text-xs text-gray-400">{evt.timestamp ? new Date(evt.timestamp).toLocaleString() : '—'}</td>
                  </tr>
                  {isOpen && hasDetail && (
                    <tr className="border-b last:border-0 bg-gray-50">
                      <td></td>
                      <td colSpan={6} className="py-3 pr-4">
                        <AuditEventDetail evt={evt} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <p className="text-gray-400 text-sm py-4 text-center">
            {search || outcomeFilter ? 'No matching events' : 'No events found'}
          </p>
        )}
      </Card>
    </div>
  );
}
